import { useCallback, useEffect, useRef, useState, type KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { PASSWORD_RESET_PATHS } from '@forecastify/shared';
import {
  Blobatar,
  Button,
  CheckboxField,
  Icon,
  PasswordInput,
  TextInput,
  Tooltip,
  useBlobatarGaze,
} from '@forecastify/ui';
import { useT, type MessageKey } from '../i18n';
import { useSession } from '../session';
import { SIGN_UP_PATH } from '../routes';
import { isCancellation, passkeysAvailable } from '../webauthn';
import { AuthField, AuthFrame } from './auth/auth-frame';
import { typingArcPoint } from './auth/gaze-arc';
import { GoogleMark } from './auth/google-mark';
import { beginPkce, takeLinkReturn } from './auth/pkce';
import { apiUrl } from '../api';
import { avatarSeed } from './identity';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ Sign in — three steps now, not one form. Items 70, 11, 2 and 1.           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **EMAIL, THEN PASSWORD, THEN — ONLY IF THE ACCOUNT HAS ONE — A CODE.** Item 70 asked for
 * the two-step form; item 11 made the third step real. `AuthFrame`'s card is 392px and a form
 * carrying five controls at once is what the split is for.
 *
 * ⚠️⚠️ **STEP ONE ASKS THE SERVER NOTHING, AND THAT IS A SECURITY PROPERTY RATHER THAN LAZINESS.**
 * The obvious two-step login looks up the address to decide what to show next — SSO, passkey,
 * password — and that lookup is an **account-enumeration oracle**: anybody can type addresses into
 * it and learn which ones exist. `hard-won.md` WP8d-3 records how carefully the rest of this flow
 * avoids exactly that (`email_not_confirmed` and `invalid_credentials` are deliberately the same
 * answer). So *Continue* is a local transition, and the first request this screen makes is always
 * a real sign-in attempt.
 *
 * ⚠️ **The password step keeps the address visible with a way back to it**, because a form that
 * has hidden what you typed is a form you cannot check before committing to it.
 *
 * ⚠️ **The code step arrives from a `mfa_required` CODE, never from a lookup** — the server
 * withholds the session after a correct password and says so, which is the only moment it is safe
 * to admit the account has a second factor.
 *
 * What the mockup adds beyond the old screen, and what became of each:
 *
 *  - **the logo above the card** — `AuthFrame`;
 *  - **`Elfelejtetted?` beside the password label** — a real link, to a real flow;
 *  - **`Maradjak bejelentkezve ezen a gépen`, checked** — real, and it governs the refresh
 *    cookie's `Max-Age`. `0028` §5 explains why the choice is stored on the session row;
 *  - **a Passkey / SSO button bar under the card** — Passkey is real. ⚠️⚠️ **SSO IS NOW GOOGLE,
 *    AND IT IS DRAWN ONLY WHEN IT IS CONFIGURED** (item 2, decision D4). It used to be a disabled
 *    button with a tooltip saying no provider existed; there is a provider now, dark until
 *    credentials arrive, and the product owner's ruling is that an unconfigured one shows
 *    **nothing at all** rather than a greyed control on the first screen anybody sees;
 *  - **`v3.4.0` in the page footer** — deliberately absent. `AuthFrame` argues it.
 *
 * ⚠️ **`chrome: 'bare'`** — a sidebar linking into a workspace you are not in yet is a sidebar of
 * links that cannot work.
 */

/** One message per code the API can answer with. Clients branch on `code`, never on `message`. */
const REASON: Readonly<Record<string, MessageKey>> = {
  invalid_credentials: 'signIn.invalid',
  rate_limited: 'signIn.rateLimited',
  unavailable: 'signIn.unavailable',
  no_membership: 'signIn.noWorkspace',
  mfa_invalid: 'signIn.mfaInvalid',
};

type Step = 'email' | 'password' | 'code';

/**
 * How far the sign-in face's eyes travel, in face units — the face is 100 across, so this is a
 * percentage of it.
 *
 * ⚠️ **Higher than the sidebar chip's 3, because this one has to read as MOVEMENT rather than
 * as presence.** The chip's face follows a pointer somebody is already watching; this one is
 * telling you it is following along, and the whole complaint it answers was that the motion was
 * too small to see. Judged rather than measured — the library's useful range is about 1.5 to 4,
 * and the ceiling is the eyes reaching the silhouette and thinning to nothing.
 */
const GAZE_TRAVEL = 3.5;

/**
 * ⚠️⚠️ **ENTER SUBMITS BECAUSE THIS SAYS SO, NOT ONLY BECAUSE THE BROWSER WOULD.** A form's
 * implicit submission is the browser's default action for Enter, and on this screen — the only one
 * carrying `username` and `current-password` — that default is exactly what a password manager's
 * inline menu takes over. UAT, 17 Oct 2026: Enter did nothing on either step in one Chrome on a Mac
 * and worked in a colleague's. Pressing the form from the key itself does not depend on what else
 * listens to it.
 *
 * ⚠️ `preventDefault` first, so the browser's own submission cannot run as well and send it twice.
 * ⚠️ Not mid-composition: Enter then commits an IME candidate rather than the form.
 */
function submitOnEnter(event: KeyboardEvent<HTMLInputElement>): void {
  if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
  event.preventDefault();
  event.currentTarget.form?.requestSubmit();
}

export function SignInScreen() {
  const t = useT();
  const session = useSession();

  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');

  /**
   * ┌──────────────────────────────────────────────────────────────────────┐
   * │ ⚠️⚠️ THE EYES FOLLOW YOUR TYPING, NOT THE CARET'S PIXEL. `gaze-arc.ts` SAYS WHY. │
   * └──────────────────────────────────────────────────────────────────────┘
   *
   * In one line: the driver aims along a unit vector, so only a target's DIRECTION moves the eyes
   * — and the real caret sits so far to the left of a wide field that its direction barely
   * changes as you type. The point aimed at here carries the same information over an arc the
   * face can express.
   *
   * ⚠️ **Re-aimed whenever the caret can have moved**: every change of the value, and `onSelect`
   * on the field for the arrow key and the click that move it without changing anything.
   *
   * ⚠️ **`'rest'` whenever the field does not have focus**, including on the email and code
   * steps where it does not exist. The face watches somebody work in a field and relaxes when they
   * leave it. Calling `lookAt` before the face has mounted queues the request rather than losing
   * it, which is what lets one effect cover a blobatar that is conditionally rendered.
   */
  const [fieldFocused, setFieldFocused] = useState(false);
  const { ref: gazeRef, lookAt } = useBlobatarGaze({ travel: GAZE_TRAVEL });
  const faceDisc = useRef<HTMLSpanElement>(null);
  const passwordField = useRef<HTMLInputElement>(null);

  const watching = step === 'password' && fieldFocused;
  const aim = useCallback(() => {
    const face = faceDisc.current;
    const field = passwordField.current;
    if (!watching || face === null || field === null) {
      lookAt('rest');
      return;
    }
    lookAt(typingArcPoint(face, field));
  }, [watching, lookAt]);

  useEffect(() => {
    aim();
  }, [aim, password]);

  /** ⚠️ The two are exclusive: a recovery key is what somebody uses INSTEAD of the six digits. */
  const [usingRecoveryKey, setUsingRecoveryKey] = useState(false);
  /**
   * ⚠️ **Checked by default, which is the mockup's own state** (`useState(true)`), and it is the
   * right default for a work tool people open every morning. Unticking it is the deliberate act, on
   * a borrowed machine.
   */
  const [remember, setRemember] = useState(true);
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  /**
   * ⚠️⚠️ **WHICH PROVIDERS EXIST IS THE SERVER'S ANSWER, NOT A BUILD FLAG.** A `VITE_` variable
   * would bake the decision into the bundle, so turning Google on would mean a rebuild rather than
   * a restart — and a staging deployment with different credentials would need its own build.
   *
   * ⚠️ **`@Public()` and unauthenticated, because this is the sign-in screen.** It returns a
   * boolean and nothing else: not the client id, not the URL.
   */
  const [google, setGoogle] = useState(false);
  useEffect(() => {
    let alive = true;
    void fetch(apiUrl('/auth/providers'))
      .then((response) => (response.ok ? response.json() : null))
      .then((body: { google?: boolean } | null) => {
        if (alive && body?.google === true) setGoogle(true);
      })
      .catch(() => {
        /* ⚠️ A silent no. An unreachable API on the sign-in screen is already about to be very
           obvious; drawing a Google button we could not confirm would be the worse guess. */
      });
    return () => {
      alive = false;
    };
  }, []);

  /** One place to interpret a result, because three controls produce the same shapes. */
  const settle = (result: { ok: boolean; code?: string }): void => {
    if (result.ok) return;
    if (result.code === 'mfa_required') {
      /* ⚠️ Not a failure message: the password was right, and this is the next step rather than a
         thing that went wrong. Showing `signIn.invalid` here would be a lie. */
      setStep('code');
      setFailure(null);
      return;
    }
    setFailure(result.code ?? 'unknown');
  };

  const submitPassword = (typed: string): void => {
    setBusy(true);
    setFailure(null);
    void session
      .signIn(email.trim(), typed, remember)
      .then(settle)
      .finally(() => {
        setBusy(false);
      });
  };

  const submitCode = (typed: string): void => {
    setBusy(true);
    setFailure(null);
    void session
      .signIn(
        email.trim(),
        password,
        remember,
        usingRecoveryKey ? { recoveryKey: typed } : { totp: typed },
      )
      .then(settle)
      .finally(() => {
        setBusy(false);
      });
  };

  /**
   * ⚠️ **`passkeysAvailable()` is false on an INSECURE ORIGIN as well as an old browser** —
   * `http://localhost` is a secure context and `http://192.168.x.x` is not, so this is correctly
   * false when a colleague opens the app over the network. It disables the button rather than
   * hiding it, because the mockup's footer bar is two columns and a bar that is sometimes one
   * column and sometimes two is a layout that looks broken in one of its states.
   */
  const passkeys = passkeysAvailable();

  const startGoogle = (): void => {
    setBusy(true);
    setFailure(null);
    /* 22 Oct 2026 · a sign-in, so a connect abandoned in this tab must not claim the callback. */
    takeLinkReturn();
    void beginPkce()
      .then(async (challenge) => {
        const response = await fetch(
          apiUrl(`/auth/oauth/google/start?code_challenge=${encodeURIComponent(challenge)}`),
        );
        if (!response.ok) throw new Error('unavailable');
        const body = (await response.json()) as { url: string };
        /* ⚠️ A full navigation, not `fetch`. The consent screen is a page for a person to look at;
           requesting it as data would render nothing and look like a dead button. */
        window.location.assign(body.url);
      })
      .catch(() => {
        setBusy(false);
        /* `crypto.subtle` is undefined on an insecure origin — `pkce.ts` argues why that is a
           refusal rather than a fallback to an unbound challenge. */
        setFailure('unavailable');
      });
  };

  return (
    <AuthFrame
      screen="sign-in"
      title={t('signIn.submit')}
      footer={
        <>
          {/**
           * ⚠️⚠️ **`flex-1` GOES ON THE TOOLTIP, NOT ON THE BUTTON — AND PUTTING IT ONLY ON THE
           * BUTTON IS WHY THIS BAR SHIPPED WRONG THE FIRST TIME.** The mockup's footer is two equal
           * halves: `<Button className="flex-1 justify-center">` inside a flex row. Wrapping each
           * button in a `Tooltip` puts an `inline-flex` span between the row and the button, so the
           * row's children are the SPANS — they size to their content, and `w-full` on the button
           * then means "full width of a content-sized wrapper", i.e. no change at all. The two
           * buttons came out narrow and left-packed instead of filling the bar.
           *
           * Reported from a screenshot, which is what CLAUDE.md §4 means by *"everything else is
           * verified by opening the app"* — no gate here has layout to measure.
           */}
          <Tooltip
            className="flex-1"
            content={passkeys ? t('signIn.withPasskey') : t('signIn.passkeyUnavailable')}
          >
            <Button
              variant="outline"
              size="lg"
              className="w-full justify-center gap-2"
              disabled={busy || !passkeys}
              onClick={() => {
                setBusy(true);
                setFailure(null);
                void session
                  .signInWithPasskey(remember)
                  .then(settle)
                  .catch((error: unknown) => {
                    /* Dismissed prompts say nothing. */
                    if (!isCancellation(error)) setFailure('unavailable');
                  })
                  .finally(() => {
                    setBusy(false);
                  });
              }}
            >
              <Icon name="key" size={14} className="text-muted-foreground" />
              {t('signIn.passkey')}
            </Button>
          </Tooltip>

          {/**
           * ⚠️⚠️ **DRAWN ONLY WHEN IT IS CONFIGURED — item 2, and the product owner's ruling.** The
           * button that stood here was a permanently disabled *SSO* with a tooltip explaining that
           * no provider existed. There is one now; until credentials arrive, `GET /auth/providers`
           * answers `false` and this renders nothing at all, because a person should never meet a
           * dead control on the first screen they see.
           *
           * ⚠️ **The bar is then one column, and that is fine.** The two-column geometry was worth
           * keeping a disabled button for when the alternative was a bar that changed shape between
           * environments; a provider that is simply absent is not a layout state to defend.
           */}
          {google && (
            <Button
              variant="outline"
              size="lg"
              className="flex-1 justify-center gap-2"
              disabled={busy}
              onClick={startGoogle}
            >
              <GoogleMark />
              {t('signIn.google')}
            </Button>
          )}
        </>
      }
    >
      {/* A real form, so Enter submits and password managers recognise it. A div with a click
          handler is the version that works for everyone except the people using a keyboard. */}
      <form
        className="flex flex-col gap-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          if (busy) return;
          /**
           * ⚠️⚠️ **THE VALUE IS READ FROM THE FIELD, NOT FROM STATE.** A browser or password manager
           * that fills a field without an `input` event leaves the state empty while the field
           * shows the address — and a submit judged on state then does nothing, with nothing on
           * screen to say why. The field is what the person sees, so it is what counts.
           */
          const form = event.currentTarget;
          /* Each step's field is named after it. */
          const input = form.elements.namedItem(step);
          const typed = input instanceof HTMLInputElement ? input.value : '';
          if (typed.trim() === '') {
            if (input instanceof HTMLInputElement) input.focus();
            return;
          }
          if (step === 'email') {
            setEmail(typed);
            setStep('password');
            setFailure(null);
          } else if (step === 'password') {
            setPassword(typed);
            submitPassword(typed);
          } else submitCode(typed);
        }}
      >
        {step === 'email' ? (
          <AuthField label={t('signIn.email')}>
            <TextInput
              type="email"
              name="email"
              autoComplete="username"
              /* The mockup's own placeholder, which teaches the shape without naming a real person. */
              placeholder={t('signIn.emailPlaceholder')}
              /* Autofocus on a single-purpose screen with one obvious first field. Anywhere else it
               * steals focus from somebody who was already reading. */
              autoFocus
              value={email}
              onKeyDown={submitOnEnter}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </AuthField>
        ) : (
          /**
           * ⚠️⚠️ **THE ADDRESS STAYS ON SCREEN WITH A WAY BACK TO IT.** A two-step form that has
           * hidden what you typed is a form you cannot check before committing to it — and a typo
           * in the address then surfaces as *"that does not match an account"*, which sends people
           * to reset a password that works.
           *
           * ⚠️ **A `<button>`, not a link**: it goes nowhere, it changes this form's state.
           */
          <div className="flex flex-col items-center gap-2.5 pb-1">
            {/**
             * ╔════════════════════════════════════════════════════════════════════════╗
             * ║ ⚠️⚠️ THIS FACE IS DRAWN WITHOUT ASKING THE SERVER ANYTHING.                     ║
             * ╚════════════════════════════════════════════════════════════════════════╝
             *
             * It is a pure function of what somebody typed, which is the whole reason the seed is
             * the email rather than the member id. The header of this file explains why the
             * obvious two-step login — look the address up, then show what fits — is an
             * account-enumeration oracle; a face that appeared only for real accounts would be
             * that same oracle drawn in pictures, and a much friendlier one to probe.
             *
             * ⚠️ **So it renders for every syntactically valid address**, whether or not an account
             * is behind it, and it is exactly the face they meet in the sidebar a second later.
             * `avatar-seed.ts` in `shared` is the one normalisation both sides run.
             */}
            {/* ⚠️ **White, like every other plate a blobatar sits on** — `AvatarFace` in
                `misc.tsx` carries the argument: the face is drawn in its own palette, and a
                tinted ground behind it is a second hue competing with the first. */}
            <span
              /* ⚠️ Measured, not assumed: `gaze-arc.ts` spans the drop from this disc to
                 the field, so the sweep stays right if the card's spacing changes. */
              ref={faceDisc}
              /**
               * ⚠️⚠️ **`flex`, NOT `grid`, AND THE DIFFERENCE IS THE OVERSIZED FACE.** A grid
               * with no template sizes its one implicit track to MAX-CONTENT — 84px — and
               * anchors it at this box's top-left. `place-items-center` then centres the face
               * within that 84px track, which is a no-op because the two are the same size,
               * and the whole track hangs 20px off the bottom-right of a 64px disc. The face
               * read as sitting low and to the right, cropped along one corner.
               *
               * ⚠️ **A flex line has no such track.** An item larger than its container under
               * `items-center justify-center` overflows both edges equally, so `overflow-hidden`
               * crops a ring rather than a corner — which is what oversizing it was for.
               */
              className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-full bg-on-id-light"
            >
              {/* ⚠️ **No `title`, because the face is DECORATIVE here.** The library draws
                  one as an SVG `<title>`, which is real text in the accessibility tree and
                  in the DOM — and the address is written in full on the line below, so a
                  title would announce it twice and put a second copy of it on the page. */}
              {/**
               * ⚠️⚠️ **THE FACE IS 84 INSIDE A 64 DISC, AND THAT IS NOT A MISTAKE.** A blobatar
               * carries its own margin inside its viewBox — sensible for a 20px row avatar, where
               * the face has to stay clear of the circle's edge to read at all. At this size the
               * same margin is a ring of empty plate around a small face, and the card's subject
               * looks like it is sitting in a room rather than filling the frame.
               *
               * ⚠️ **Oversizing the face rather than shrinking the plate** keeps the disc the size
               * the card's rhythm wants and lets the parent's `overflow-hidden` do the cropping,
               * which is what a portrait is. `place-items-center` above keeps it centred as it
               * grows.
               */}
              <span className="size-[84px] shrink-0">
                <Blobatar seed={avatarSeed(email)} size={84} animate="always" svgRef={gazeRef} />
              </span>
            </span>
            {/* ⚠️ **The address stays on screen with a way back to it** — the note this block
                replaced said why, and it still holds: a two-step form that has hidden what you
                typed is a form you cannot check before committing to it, and a typo then surfaces
                as *"that does not match an account"*, which sends people to reset a password that
                works. ⚠️ A `<button>`, not a link: it goes nowhere, it changes this form's state. */}
            <span className="text-muted-foreground max-w-full truncate text-center text-[12.5px]">
              {t('signIn.loggingInAs')}{' '}
              <span className="text-foreground font-medium">{email.trim()}</span>{' '}
              <button
                type="button"
                className="text-primary font-medium"
                onClick={() => {
                  setStep('email');
                  setPassword('');
                  setCode('');
                  setFailure(null);
                }}
              >
                {t('signIn.changeEmail')}
              </button>
            </span>
          </div>
        )}

        {step === 'password' && (
          <AuthField
            label={t('signIn.password')}
            hint={
              /**
               * ⚠️ **A `<Link>`, not a button.** `button.tsx`: *"a control that NAVIGATES is not a
               * button"* — an anchor is what survives a middle-click and what the reachability check
               * can see as an entrance. This is the only entrance `/forgot-password` has.
               */
              <Link
                to={PASSWORD_RESET_PATHS.request}
                className="text-primary text-[11px] leading-4 font-medium"
              >
                {t('signIn.forgot')}
              </Link>
            }
          >
            <PasswordInput
              name="password"
              showLabel={t('signIn.showPassword')}
              hideLabel={t('signIn.hidePassword')}
              autoComplete="current-password"
              placeholder="••••••••"
              autoFocus
              ref={passwordField}
              onFocus={() => {
                setFieldFocused(true);
              }}
              onBlur={() => {
                setFieldFocused(false);
              }}
              /* ⚠️ The caret moved without the value changing — an arrow key, a click, a
                 select-all. The effect above covers typing; this covers the rest. */
              onSelect={aim}
              value={password}
              onKeyDown={submitOnEnter}
              onChange={(event) => {
                setPassword(event.target.value);
              }}
            />
          </AuthField>
        )}

        {step === 'code' && (
          <>
            <AuthField label={usingRecoveryKey ? t('signIn.recoveryKey') : t('signIn.code')}>
              <TextInput
                name="code"
                inputMode={usingRecoveryKey ? 'text' : 'numeric'}
                autoComplete={usingRecoveryKey ? 'off' : 'one-time-code'}
                autoFocus
                className={
                  usingRecoveryKey
                    ? 'text-center tracking-[0.12em] uppercase'
                    : 'text-center tracking-[0.4em] tabular-nums'
                }
                value={code}
                onKeyDown={submitOnEnter}
                onChange={(event) => {
                  /* ⚠️ The six-digit field refuses anything else as it is typed — a TOTP code has
                     exactly one shape, and letting somebody finish typing a wrong-shaped one only
                     to be refused by the server wastes a round trip and a code. The recovery key
                     is left alone: `hashRecoveryCode` normalises case and punctuation server-side,
                     so a pasted key with its dash is correct as it stands. */
                  setCode(
                    usingRecoveryKey
                      ? event.target.value
                      : event.target.value.replace(/\D/gu, '').slice(0, 6),
                  );
                }}
              />
            </AuthField>

            <p className="text-muted-foreground text-[11px] leading-4">
              {usingRecoveryKey ? t('signIn.recoveryKeyHint') : t('signIn.codeHint')}{' '}
              <button
                type="button"
                className="text-primary font-medium"
                onClick={() => {
                  setUsingRecoveryKey((using) => !using);
                  setCode('');
                  setFailure(null);
                }}
              >
                {usingRecoveryKey ? t('signIn.useCode') : t('signIn.useRecoveryKey')}
              </button>
            </p>
          </>
        )}

        {/**
         * ⚠️⚠️ **`CheckboxField`, NOT A HAND-ROLLED ROW — AND THE FIRST ATTEMPT HERE WAS THE
         * BUG IT EXISTS TO PREVENT.** `Checkbox` is a `<button role="checkbox">` carrying its name
         * in `aria-label`, so a visible label has to be a sibling. Wrapping both in a clickable
         * span, which is the obvious way to make the text toggle it, means a click on the BOX runs
         * the box's handler and then bubbles to the wrapper's — two toggles, net nothing, and the
         * checkbox appears not to work at all while the text works fine.
         *
         * `CheckboxField` puts the handler on the text alone and marks it `aria-hidden` so the name
         * is not announced twice. Its row is `flex items-center gap-2` at 12.5px, which is the
         * mockup's own.
         *
         * ⚠️ **Not on the code step.** By then the choice has already been made on the step below
         * it, and a checkbox that reappears invites somebody to wonder whether it counts twice.
         */}
        {step !== 'code' && (
          <CheckboxField
            checked={remember}
            onCheckedChange={setRemember}
            label={t('signIn.remember')}
          />
        )}

        {failure !== null && (
          /**
           * ⚠️ **`role="alert"`, so it is announced.** A message that only appears visually is a
           * message a screen-reader user never learns about — and this one is the difference
           * between "try again" and "your account is not in any workspace".
           */
          <p role="alert" className="text-destructive text-[12px]">
            {t(REASON[failure] ?? 'error.unknown')}
          </p>
        )}

        {/**
         * `size="lg"` and full width — the mockup's own.
         *
         * ⚠️⚠️ **DISABLED ONLY WHILE BUSY, NEVER FOR AN EMPTY FIELD.** Enter in a field presses the
         * form's first submit button, and a DISABLED one swallows it without a sound — HTML's rule
         * for implicit submission. Judged on state, a field filled without an `input` event left
         * this disabled under an address in plain view. An empty field is `onSubmit`'s to refuse.
         */}
        <Button type="submit" size="lg" className="w-full" disabled={busy}>
          {busy
            ? t('signIn.working')
            : step === 'email'
              ? t('signIn.continue')
              : t('signIn.submit')}
        </Button>

        {/**
         * ⚠️⚠️ **THE ONLY ENTRANCE TO `/sign-up` IN THE WHOLE APPLICATION, AND WITHOUT IT S2 SHIPS A
         * SCREEN NOBODY CAN REACH.** That is the failure CLAUDE.md §4 names first — *"seven screens
         * once shipped built, working and reachable only by typing a URL"* — and a signed-out screen
         * cannot be reached from the sidebar, because the sidebar only exists after signing in. So
         * the sign-in form is the entrance, and `routes.test.tsx` asserts this control exists rather
         * than trusting it to stay.
         *
         * ⚠️ **It stays on every step**, because the step somebody discovers they have no account
         * on is the password one.
         *
         * ⚠️ **A `<Link>`, for the reason the forgot-password hint above states.** It arrived in S2
         * as a `Button` with `useNavigate`, which `button.tsx` rules out for anything that
         * navigates; C18's screen had already settled the pattern, so this follows it.
         */}
        <p className="text-muted-foreground pt-1 text-center text-[11px] leading-4">
          <Link to={SIGN_UP_PATH} className="text-primary font-medium">
            {t('signIn.noAccount')}
          </Link>
        </p>
      </form>
    </AuthFrame>
  );
}
